import { PRP_ENABLED } from "@/lib/hidden-features";

/** Assigned RBAC roles that belong in the PRP workspace. */
const PRP_ROLES = new Set(["prp_rc", "prp_director"]);

/** JWT `rls` claim - assigned RBAC roles, including prp_rc / prp_director. */
export function rolesFromAccessToken(token?: string | null): string[] {
  if (!token) return [];
  try {
    const segment = token.split(".")[1];
    if (!segment) return [];
    const payload = JSON.parse(atob(segment.replace(/-/g, "+").replace(/_/g, "/"))) as {
      role?: unknown;
      rls?: unknown;
    };
    const roles: string[] = [];
    if (typeof payload.role === "string") roles.push(payload.role);
    if (Array.isArray(payload.rls)) {
      for (const role of payload.rls) {
        if (typeof role === "string") roles.push(role);
      }
    }
    return roles;
  } catch {
    return [];
  }
}

export function isPrpAssigned(
  user?: {
    role?: string;
    selected_roles?: string[];
  } | null,
  tokenRoles?: string[],
): boolean {
  if (!PRP_ENABLED) return false;
  const extra = tokenRoles ?? [];
  const roles = [user?.role, ...(user?.selected_roles ?? []), ...extra];
  return roles.some((role) => role != null && PRP_ROLES.has(role));
}

/**
 * Mirrors the server rule for blocked-off time: providers and PRP counselors
 * (not also a Program Director or admin) only read and manage their own.
 */
export function ownScheduleOnly(role?: string, tokenRoles: string[] = []): boolean {
  if (role === "provider") return true;
  if (role === "org_admin" || role === "super_admin") return false;
  if (tokenRoles.includes("prp_director") || tokenRoles.includes("org_admin")) return false;
  return tokenRoles.includes("prp_rc");
}

/** Primary logins whose home is PRP, not the clinic appointment board. */
export function wantsPrpHome(role?: string): boolean {
  if (!PRP_ENABLED) return false;
  return role === "staff" || role === "prp_rc" || role === "prp_director";
}

/**
 * Staff / PRP logins open the PRP workspace. The account default of
 * /dashboard is the clinic appointment board and is ignored for these roles.
 */
export function resolveStaffLanding(opts: {
  role?: string;
  canUsePrp: boolean;
  prpAssigned: boolean;
  landingPage?: string;
}): string {
  if (wantsPrpHome(opts.role)) {
    const page = opts.landingPage;
    if (page && page !== "/dashboard" && page !== "/") return page;
    return "/prp";
  }
  return opts.landingPage || "/dashboard";
}

export function readLandingPage(preferences?: string): string | undefined {
  if (!preferences) return undefined;
  try {
    const parsed = JSON.parse(preferences) as { landingPage?: unknown };
    return typeof parsed.landingPage === "string" ? parsed.landingPage : undefined;
  } catch {
    return undefined;
  }
}
